/**
 * The app's constants: a gardener's preferences
 * and their defaults, which components write debug lines, the plant grid's
 * status classes, the numbers photos are tuned by, and the hemisphere's.
 *
 * Constants only: no functions beyond debugLog, which reads them.
 *
 * The numbers someone might tune each carry a kept-values group (scripts/kept-values.md), for when config
 * comes from the database. Newer tunables live in src/config/tunables, one
 * file per area; these older catalogues stay here because many places import
 * them by these names.
 *
 * Used by: nearly forty files, mostly for debugLog. Preferences and
 * UserConfig by the user-config store and provider; APP_CONSTANTS.PHOTO by PhotoProcessor,
 * uploadablePhoto and the camera; and UI_CONSTANTS by PlantGrid.
 *
 * Env vars: none. The photo store's address is the server's (limits.SPACES_BASE_URL, lib/growSettings).
 *
 * Surprising: Preferences is declared twice, here and in
 * shared/context/userConfigStore, to break an import cycle, so a new
 * preference must be added in both. Turning a DEBUG_COMPONENTS switch on in
 * a build that ships prints gardeners' data to the console (see ApiClient).
 *
 * @module config
 */

import { Language } from '@/shared/types/i18n';

/** The canvas's image smoothing, as the DOM names it (PhotoProcessor). */
type ImageSmoothingQuality = 'low' | 'medium' | 'high';

//NEVER include functions in the config file. This is for constants only.


/**
 * A gardener's preferences, kept on their account (routes/user-settings).
 * Declared here as well as in userConfigStore to avoid a circular import.
 */
export interface Preferences {
  /** The app's look; retired names are normalised by themeUtils. */
  theme: 'grow' | 'light' | 'dark' | 'fieldnotes';
  /** The app's language. */
  language: Language;
  /** The root text size (themeUtils applyFontSize). */
  fontSize: 'small' | 'medium' | 'large';
  /** Which way round the seasons run. */
  hemisphere: Hemisphere;
  /** The unit temperatures are shown in; everything is stored in Fahrenheit. */
  temperatureUnit: 'C' | 'F';
  /** How many days ahead Today's Coming up looks, 0 to 7; null for the Grow settings' default. */
  headsUpDays: number | null;
  /** "Move my plants' stages for me": Grow's automatic stages, on unless switched off. */
  autoStages: boolean;
  /** What counts as the season for the season review, as the account keeps it; absent until the account is read. */
  seasonReviewBy?: 'growing' | 'calendar';
}

/** The signed-in gardener's id and preferences. */
export interface UserConfig {
  /** The Grow user id, or null while nobody is signed in. */
  userId: number | null;
  /** Their preferences, or the defaults until they are read. */
  preferences: Preferences;
  /** The garden's town, as saved with the ZIP (the profile's City); null until there is one. */
  city: string | null;
  /** The garden's state, two letters ("TN"), as saved with the ZIP; null until there is one. */
  state: string | null;
}

/**
 * Nobody signed in, at the default preferences: what a new account and a
 * signed-out page start with.
 * @dbCandidate
 */
export const DEFAULT_USER_CONFIG: UserConfig = {
  userId: null,
  city: null,
  state: null,
  preferences: {
    theme: 'grow',
    hemisphere: 'north',
    fontSize: 'medium',
    language: 'en',
    temperatureUnit: 'F',
    headsUpDays: null,
    autoStages: true
  }
};

/**
 * Which components write debug lines: all off in what is shipped. Not a
 * database candidate: a switch for whoever is debugging, in their own build.
 */
export const DEBUG_COMPONENTS = {
  /*
   * The API client, off.
   *
   * It logged unconditionally: the base URL and the dev Firebase UID at module
   * load, then every request, every token decision, and — the reason this
   * needed a switch rather than a tidy-up — every response body in full. That
   * is the gardener's whole garden, their profile and their email address,
   * printed to the console of a shipped application.
   */
  ApiClient: false,
  /*
   * The rest of the app's own logging, off.
   *
   * These were raw console.log, so they ran in production and ran on every
   * render: a single page load put more than twenty thousand lines into the
   * console, which is the same as having no console at all — the one real
   * warning in there cannot be found. They keep their switches so the next
   * person debugging auth or the theme can turn exactly that back on.
   */
  UserConfig: false,
  Theme: false,
  Tooltip: false,
  LoginPage: false,
  FirebaseInit: false,
  PhotoUploadDialog: false,
  PlantsContext: false,
  AuthContext: false,
  PhotoProcessor: false,
  ImageUtils: false,
  StageService: false,
  UserProfile: false,
  DateUtils: false,
  // The photo dialog, broken up: each piece logs under its own name so a
  // camera problem and a note problem are not the same switch.
  useCamera: false,
  useGooglePhotos: false,
  usePhotoChoice: false,
  usePhotoNote: false,
  PhotoTray: false,
  TrayUploader: false,
  GoogleOneTap: false,
  // Today's postcode box and its waitlist offer.
  TodayPostcode: false
} as const;

/**
 * A debug line, only for a component switched on in DEBUG_COMPONENTS.
 *
 * @param component - Who is writing it.
 * @param args - What to write.
 */
export const debugLog = (component: keyof typeof DEBUG_COMPONENTS, ...args: unknown[]): void => {
  if (DEBUG_COMPONENTS[component]) {
    console.log(`[${component}]`, ...args);
  }
};

/** Class names the plant grid's loading and error lines wear (PlantGrid). */
export const UI_CONSTANTS = {
  GRID_STYLES: {
    LOADING: "text-muted-foreground",
    ERROR: "text-destructive"
  }
};

/**
 * How a photo is shrunk on the device: dimensions and compression. The largest
 * file and the kinds of picture taken are the server's (limits.PHOTO_MAX_MB and
 * limits.PHOTO_TYPES, lib/growSettings).
 */
export const APP_CONSTANTS = {
  PHOTO: {
    /** @kept K4 Widest a shrunk photo is, pixels; also the camera's ideal (PhotoProcessor, useCamera). */
    MAX_WIDTH: 2048,  // Maximum photo width - PhotoProcessor.ts
    /** @kept K4 Tallest a shrunk photo is, pixels; also the camera's ideal (PhotoProcessor, useCamera). */
    MAX_HEIGHT: 2048,  // Maximum photo height - PhotoProcessor.ts
    /**
     * @kept K4 The JPEG quality a shrunk photo is first saved at, 0 to 1. 0.85 since B82, when every photo
     * over 2048 pixels began to be shrunk: small enough to send quickly, and seed-packet text stays readable.
     */
    QUALITY: 0.85,  // Photo compression quality - PhotoProcessor.ts
    /**
     * @kept K4 What a shrunk photo's transparent parts are laid on: JPEG has no transparency, and a canvas
     * saved as JPEG turns them black. White, as the server lays its own copies (B82).
     */
    FLATTEN_BACKGROUND: '#ffffff',
    /** @kept K4 The lowest JPEG quality tried before giving up on the size, 0 to 1. */
    MIN_QUALITY: 0.1,  // Minimum photo quality - PhotoProcessor.ts
    /** @kept K4 How much quality drops at each retry, 0 to 1. */
    QUALITY_REDUCTION_STEP: 0.1,  // Quality reduction increment - PhotoProcessor.ts
    /** @kept K4 The canvas's smoothing when a photo is drawn smaller. */
    SMOOTHING: 'high' as ImageSmoothingQuality,  // Image smoothing quality - PhotoProcessor.ts
    /**
     * Bytes in a kilobyte, and kilobytes in a megabyte: PhotoProcessor
     * multiplies by it twice. Misnamed, but right where it is used.
     */
    BYTES_PER_MB: 1024,  // Bytes per megabyte conversion - PhotoProcessor.ts
  },
};

/** North or south of the equator, for the seasons. */
export type Hemisphere = 'north' | 'south';

